import React, { useState, useEffect } from "react";
import { tss } from "tss-react";
import GenericInputWrapper from "./GenericInputWrapper";
import { TextField } from "@mui/material";
import { PhoneNumber, isPossiblePhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import ReactPhoneInput from 'react-phone-input-material-ui';

interface IGenericInputProps {
    title: string;
    type: string;
    value: any;
    onChange: (value: any) => void;
    error?: string;
    name: string;
    onBlur?: (name: string) => void;
    disabled?: boolean;
    size?: "small" | "medium";
}

const useStyles = tss.create({
    label: {
        display: "block",
    },
    input: {
        width: "100%"
    },
    errorInput: {
        // border: "1px solid red"
    },
    error: {
        // display: "block",
        // color: "red",
    },
});

const GenericPhoneInput: React.FC<IGenericInputProps> = ({ 
    title, 
    type, 
    value, 
    disabled, 
    error, 
    size = "medium", 
    onChange, 
    name, 
    onBlur = () => { } 
}) => {
    const { classes, cx } = useStyles();
    const [internalError, setInternalError] = useState<string>('');

    // Validate phone number with better format handling
    const validatePhoneNumber = (phoneNumber: string): string => {
        if (!phoneNumber || phoneNumber.trim() === '') {
            return '';
        }

        try {
            // The react-phone-input-material-ui might return phone numbers in different formats
            // Let's try to handle the format properly
            let phoneToValidate = phoneNumber;
            
            // If the phone number doesn't start with +, add it
            if (!phoneToValidate.startsWith('+')) {
                phoneToValidate = '+' + phoneToValidate;
            }

            // Check if it's a possible phone number
            if (!isPossiblePhoneNumber(phoneToValidate)) {
                return 'Invalid phone number';
            }

            // Additional validation using parsePhoneNumber for more thorough checking
            const parsed = parsePhoneNumber(phoneToValidate);
            if (!parsed || !parsed.isValid()) {
                return 'Invalid phone number';
            }

            return '';
        } catch (error) {
            return 'Invalid phone number';
        }
    };

    const handleChange = (phoneNumber: string) => {
        // Clear internal error when user types
        if (internalError) {
            setInternalError('');
        }
        onChange(phoneNumber);
    };

    const handleBlur = () => {
        // Only validate and set internal error if there's no external error
        if (!error) {
            const validationError = validatePhoneNumber(value);
            setInternalError(validationError);
        }
        onBlur(name);
    };

    // Clear internal error when external error changes
    useEffect(() => {
        if (error) {
            setInternalError('');
        }
    }, [error]);

    // Use external error first, then internal error
    const displayError = error || internalError;

    return (
        <GenericInputWrapper title={""} error={displayError} name={name}>
            <ReactPhoneInput
                value={value}
                placeholder=""
                component={TextField}
                inputProps={{ 
                    variant: "outlined", 
                    error: !!displayError, 
                    size: size, 
                    label: title 
                }}
                disabled={disabled}
                inputClass={cx(classes.input, !!displayError ? classes.errorInput : undefined)}
                onChange={handleChange}
                onBlur={handleBlur}
                excludeCountries={["il"]}
                country={"lb"}
                countryCodeEditable={false}
            />
        </GenericInputWrapper>
    );
};

export default GenericPhoneInput;